<script lang="ts" setup>
import PersonaIcon from "@/components/icon/PersonaIcon.vue";
import { Image, showFailToast, showLoadingToast, showSuccessToast } from "vant";
import PlusIcon from "@/components/icon/PlusIcon.vue";
import { computed, ref } from "vue";
import { MediaUploadFileItem } from "@/types/component";
import MediaSelectPopup from "@/components/mobile/media/MediaSelectPopup.vue";
import { Media, StoryBook, Visibility } from "@/types/entity";
import { useStoryBook } from "@/stores/story-book";
import VisibilitySelect from "@/components/mobile/common/VisibilitySelect.vue";
import { useI18n } from "vue-i18n";
import { PickerConfirmEventParams } from "vant/es";
import { useMate } from "@/stores/mate";

const props = withDefaults(
  defineProps<{
    storyBookId?: number;
    title?: string;
    description?: string;
    media?: Media;
    visibility: Visibility;
  }>(),
  {
    title: "",
    description: "",
  }
);
const emit = defineEmits<{
  (e: "onSuccess", storyBook: StoryBook): void;
  (e: "onCancel"): void;
}>();

const { t } = useI18n();

// State
const storyBookStore = useStoryBook();
const title = ref(props.title);
const description = ref(props.description);
const mediaList = ref<MediaUploadFileItem[]>(
  props.media
    ? [
        {
          mediaId: props.media!.id,
          url: props.media!.url,
          status: "done",
        } as MediaUploadFileItem,
      ]
    : []
);
const visibility = ref<Visibility>(2);
const showMediaSelection = ref(false);

const showTypeSelect = ref(false);
const _type = ref(1);
const type = computed(() => t(`story.type.${_type.value}`));

const columns = Array.from({ length: 29 }).map((_, idx) => ({
  text: t(`story.type.${idx + 1}`),
  value: idx + 1,
}));
const onConfirm = (value: PickerConfirmEventParams) => {
  _type.value = value.selectedOptions[0]!.value! as number;
  showTypeSelect.value = false;
};

// EventHandler
const onConfirmStoryBookSaveInfo = async () => {
  // 1. 校验
  if (!title.value)
    return showFailToast({ message: t("Message.enterStoryName") });
  if (!description.value)
    return showFailToast({ message: t("Message.enterStoryDesc") });
  // 2. 提交
  const loadingToast = showLoadingToast({
    message: props.storyBookId
      ? t("Message.creatingStory")
      : t("Message.savingStory"),
    duration: 0,
  });
  const params = {
    title: title.value,
    description: description.value,
    mediaId: mediaList.value[0]?.mediaId,
    visibility: visibility.value,
    mateId: useMate().userMate.id,
  };
  try {
    if (props.storyBookId) {
      const storyBook = await storyBookStore.updateStoryBook(
        props.storyBookId,
        params
      );
      showSuccessToast(t("Message.storyModifiedSuccessfully"));
      emit("onSuccess", storyBook);
    } else {
      const storyBook = await storyBookStore.createStoryBook(params);
      showSuccessToast(t("Message.storyCreatedSuccessfully"));
      emit("onSuccess", storyBook);
      if (storyBookStore.storyType !== 0) {
        storyBookStore.getStoryBookList(false, 0);
        storyBookStore.storyType = 0;
      }
    }
  } finally {
    loadingToast.close();
  }
};
const reset = () => {
  title.value = props.title;
  description.value = props.description;
  mediaList.value = props.media
    ? [
        {
          mediaId: props.media!.id,
          url: props.media!.url,
          status: "done",
        } as MediaUploadFileItem,
      ]
    : [];
};

export interface StoryBookInfoEditExpose {
  reset: () => void;
}

defineExpose<StoryBookInfoEditExpose>({ reset });
</script>
<template>
  <div
    class="flex flex-col items-center justify-center w-full h-full gap-5 px-10"
  >
    <div class="flex flex-col items-center justify-center gap-2 w-full">
      <persona-icon class="w-[20%] text-base-content/50" />
      <div class="text-lg font-bold text-base-content/50">
        {{ storyBookId ? $t("story.editStory") : $t("story.newStory") }}
      </div>
    </div>
    <div class="flex flex-col gap-2 w-full">
      <!-- <VanField
        class="bg-base-100 border  rounded"
        v-model="type"
        is-link
        readonly
        name="picker"
        label="类型"
        placeholder="点击选择类型"
        @click="showTypeSelect = true"
      /> -->
      <div class="flex flex-col gap-2">
        <span class="font-bold">{{ $t("story.name") }}</span>
        <input v-model="title" class="input input-bordered" />
      </div>

      <div class="flex flex-col gap-2">
        <div class="flex justify-between items-center">
          <span class="font-bold">{{ $t("story.storyDescription") }}</span>
          <!-- <button
            class="btn  btn-xs rounded"
            @click="clickAutoGenerate"
          >
            自动生成
          </button> -->
        </div>
        <textarea v-model="description" class="textarea textarea-bordered" />
      </div>
      <div class="flex flex-col gap-2">
        <span class="font-bold">{{ $t("story.cover") }}</span>
        <div class="w-full flex justify-center">
          <div @click="showMediaSelection = true">
            <Image
              v-if="mediaList.length > 0"
              :src="mediaList[0].url!"
              class="w-full aspect-[1/1]"
              fit="cover"
              crossorigin="anonymous"
            />
            <div v-else class="btn flex items-center justify-center">
              <plus-icon class="icon-xl text-base" />
            </div>
          </div>
        </div>
      </div>
      <div class="flex flex-col gap-2">
        <span class="font-bold">{{ $t("story.visibility") }}</span>
        <visibility-select v-model:visibility="visibility" />
      </div>
    </div>
    <div class="flex flex-col gap-2 justify-between items-center w-full">
      <button
        class="btn rounded btn-primary btn-sm w-full"
        @click="onConfirmStoryBookSaveInfo"
      >
        {{ $t("Buttons.save") }}
      </button>
      <button class="btn rounded btn-sm w-full" @click="emit('onCancel')">
        {{ $t("Buttons.cancel") }}
      </button>
    </div>
  </div>
  <media-select-popup
    v-model:media-list="mediaList"
    v-model:show="showMediaSelection"
    :cropAspectRatio="1"
    :enable-crop="true"
    :max-count="1"
    :multiple="false"
    :upload-type="8"
    media-type="img"
    replace
  />

  <VanPopup v-model:show="showTypeSelect" position="bottom">
    <VanPicker
      :columns="columns"
      @cancel="showTypeSelect = false"
      @confirm="onConfirm"
    />
  </VanPopup>
</template>
